// /email-automations — enable/disable every email automation in the system.
// All staff can view; only admins + super admin can change toggles.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Zap, Users, BellRing, ShieldCheck, Sunrise, Mail, Plus, Lock, Pencil } from "lucide-react";

const SECTIONS = [
  { cat: "staff",    title: "Staff notifications",     icon: Users,     hint: "Keeps the team in the loop on quotes and deals" },
  { cat: "alerts",   title: "Office alerts",           icon: BellRing,  hint: "Enquiries, payments and site alerts" },
  { cat: "security", title: "Security",                icon: ShieldCheck, hint: "Account and access alerts" },
  { cat: "digest",   title: "Daily digests",           icon: Sunrise,   hint: "Scheduled 6 AM summaries" },
];

const Row = ({ id, name, desc, to, enabled, canEdit, busy, onToggle, extra, customised, onEdit }) => (
  <div className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/50"
    data-testid={`automation-row-${id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-slate-900">{name}</span>
        {to && <Badge className="rounded-sm font-normal text-[10px] bg-slate-50 text-slate-500 border border-slate-200">→ {to}</Badge>}
        {customised && <Badge className="rounded-sm font-normal text-[10px] bg-violet-50 text-violet-700 border border-violet-200">customised</Badge>}
        {extra}
      </div>
      {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
    </div>
    {canEdit && onEdit && (
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
        onClick={onEdit} data-testid={`automation-edit-${id}`}>
        <Pencil size={12} />
      </Button>
    )}
    <span className={`text-[10px] uppercase tracking-wide font-semibold ${enabled ? "text-emerald-600" : "text-slate-400"}`}>
      {enabled ? "On" : "Off"}
    </span>
    {busy ? (
      <Loader2 size={16} className="animate-spin text-slate-400" />
    ) : (
      <Switch checked={enabled} disabled={!canEdit} onCheckedChange={(v) => onToggle(id, v)}
        data-testid={`automation-toggle-${id}`} />
    )}
  </div>
);

export default function EmailAutomations() {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const canEdit = ["admin", "super_admin"].includes(user?.role);
  const [busyKey, setBusyKey] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["email-automations"],
    queryFn: async () => (await api.get("/email-automations")).data,
  });

  const toggle = async (key, enabled) => {
    setBusyKey(key);
    try {
      await api.put(`/email-automations/${key}`, { enabled });
      toast.success(`${enabled ? "Enabled" : "Disabled"}`);
      qc.invalidateQueries({ queryKey: ["email-automations"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusyKey(null);
    }
  };

  const builtins = data?.builtins || [];
  const systemRules = data?.system_rules || [];
  const customRules = data?.custom_rules || [];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="email-automations-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[2px] text-cyan-600">Communications</div>
          <h1 className="font-display font-bold text-slate-900 text-3xl leading-tight mt-1">Email automations</h1>
          <p className="text-xs text-slate-500 mt-1">
            Every automated email the system sends. Switch any of them on or off — changes apply instantly.
          </p>
          {!canEdit && (
            <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1" data-testid="read-only-note">
              <Lock size={11} /> View only — ask an admin to change these settings.
            </p>
          )}
        </div>
        <Button onClick={() => nav("/automation")} className="rounded-sm h-9 bg-cyan-700 hover:bg-cyan-800"
          data-testid="new-email-automation-btn">
          <Plus size={14} className="mr-1.5" /> New email automation
        </Button>
      </header>

      {isLoading && <Card className="rounded-sm p-8 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></Card>}

      {!isLoading && SECTIONS.map(({ cat, title, icon: Icon, hint }) => {
        const rows = builtins.filter((b) => b.category === cat);
        if (!rows.length) return null;
        return (
          <section key={cat} data-testid={`section-${cat}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} className="text-cyan-700" />
              <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
              <span className="text-[11px] text-slate-400">— {hint}</span>
            </div>
            <Card className="rounded-sm p-0 overflow-hidden">
              {rows.map((b) => (
                <Row key={b.key} id={b.key} name={b.name} desc={b.desc}
                  to={b.recipients?.length ? b.recipients.join(", ") : b.to}
                  enabled={b.enabled} canEdit={canEdit} busy={busyKey === b.key} onToggle={toggle}
                  customised={b.customised}
                  onEdit={b.editable === false ? null : () => setEditItem({
                    ...b, recipients_text: (b.recipients || []).join(", "),
                    subject_text: b.subject || "", body_text: b.body || "",
                  })} />
              ))}
            </Card>
          </section>
        );
      })}

      {!isLoading && systemRules.length > 0 && (
        <section data-testid="section-system-rules">
          <div className="flex items-center gap-2 mb-2">
            <Mail size={14} className="text-cyan-700" />
            <h2 className="text-sm font-semibold text-slate-900">Client emails</h2>
            <span className="text-[11px] text-slate-400">— automatic emails sent to your clients</span>
          </div>
          <Card className="rounded-sm p-0 overflow-hidden">
            {systemRules.map((r) => (
              <Row key={r.id} id={r.id} name={r.name} desc={r.action_title} to="Client"
                enabled={r.enabled !== false} canEdit={canEdit} busy={busyKey === r.id} onToggle={toggle} />
            ))}
          </Card>
        </section>
      )}

      {!isLoading && (
        <section data-testid="section-custom-rules">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={14} className="text-cyan-700" />
            <h2 className="text-sm font-semibold text-slate-900">Custom email automations</h2>
            <span className="text-[11px] text-slate-400">— built in the Automation builder</span>
          </div>
          <Card className="rounded-sm p-0 overflow-hidden">
            {customRules.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400" data-testid="custom-rules-empty">
                No custom email automations yet. Use <b>New email automation</b> to create one in the
                Automation builder — it supports AI drafting and will appear here.
              </div>
            ) : customRules.map((r) => (
              <Row key={r.id} id={r.id} name={r.name} desc={r.action_title}
                extra={r.trigger_event && <Badge className="rounded-sm font-normal text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200">{r.trigger_event}</Badge>}
                enabled={r.enabled !== false} canEdit={canEdit} busy={busyKey === r.id} onToggle={toggle} />
            ))}
          </Card>
        </section>
      )}

      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="email-automation-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Edit — {editItem?.name}</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Recipients (comma-separated emails — blank = default: {editItem.to})</label>
                <Input data-testid="edit-recipients-input" value={editItem.recipients_text}
                  onChange={(e) => setEditItem({ ...editItem, recipients_text: e.target.value })}
                  placeholder="blank keeps the default recipients" className="rounded-sm mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Subject (blank = default)</label>
                <Input data-testid="edit-subject-input" value={editItem.subject_text}
                  onChange={(e) => setEditItem({ ...editItem, subject_text: e.target.value })}
                  placeholder="blank keeps the default subject" className="rounded-sm mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Message body (blank = default)</label>
                <Textarea rows={6} data-testid="edit-body-input" value={editItem.body_text}
                  onChange={(e) => setEditItem({ ...editItem, body_text: e.target.value })}
                  placeholder="Write plain text — each line becomes a paragraph." className="rounded-sm mt-1 text-sm" />
              </div>
              {(editItem.placeholders || []).length > 0 && (
                <p className="text-[10px] text-slate-400">
                  Placeholders for subject & body: {editItem.placeholders.map((p) => `{{${p}}}`).join(" ")}
                </p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-sm text-slate-500 mr-auto" data-testid="edit-reset-btn"
              onClick={() => setEditItem({ ...editItem, recipients_text: "", subject_text: "", body_text: "" })}>
              Reset to defaults
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button data-testid="edit-save-btn" className="rounded-sm bg-cyan-700 hover:bg-cyan-800" onClick={async () => {
              try {
                await api.put(`/email-automations/${editItem.key}`, {
                  recipients: editItem.recipients_text.split(",").map((s) => s.trim()).filter(Boolean),
                  subject: editItem.subject_text || "",
                  body: editItem.body_text || "",
                });
                toast.success("Saved");
                setEditItem(null);
                qc.invalidateQueries({ queryKey: ["email-automations"] });
              } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
